/**
 * Collapse concurrent calls onto ONE in-flight promise.
 *
 * Written for token refresh, where "just call it again" is not idempotent: the
 * refresh token ROTATES, and the server treats a second use of an
 * already-rotated token as theft and revokes the whole family
 * (SECURITY_&_UAC §3.2). Two overlapping refreshes therefore do not merely
 * duplicate work — they sign the user out.
 *
 * The slot is passed in (a `useRef`, or any `{ current }` box) so a React
 * component can own the lifetime across renders while this stays a pure,
 * testable function.
 *
 * The slot is cleared when the promise SETTLES, not when it resolves: a
 * rejected attempt must not wedge the slot and block every later call.
 */
export function shareInFlight<T>(
  slot: { current: Promise<T> | null },
  run: () => Promise<T>,
): Promise<T> {
  if (slot.current) return slot.current
  const started = run().finally(() => {
    slot.current = null
  })
  slot.current = started
  return started
}
